import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../utils/axiosConfig';
import io from 'socket.io-client';
import { playBeep } from '../utils/audioUtils';
import { formatBrasiliaDate, formatBrasiliaTime } from '../utils/dateUtils';
import './Dashboard.css';

let socket;

const Dashboard = () => {
  // Use a ref to store the latest data to prevent flickering
  const latestDataRef = useRef({
    trucks: [],
    checkers: [],
    partners: []
  });
  
  const [trucks, setTrucks] = useState([]);
  const [checkers, setCheckers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activeTab, setActiveTab] = useState('aguardando');
  const [blinkingTruck, setBlinkingTruck] = useState(null);
  const [showMoveToDescargaModal, setShowMoveToDescargaModal] = useState(false);
  const [currentTruckId, setCurrentTruckId] = useState(null);
  const [moveToDescargaData, setMoveToDescargaData] = useState({
    dockNumber: '',
    checker: '',
    volume: ''
  });
  const [timeFilter, setTimeFilter] = useState('tudo');
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTruck, setEditingTruck] = useState(null);
  const [editFormData, setEditFormData] = useState({
    trailerPlate: '',
    truckPlate: '',
    driverName: '',
    cpf: '',
    partner: '',
    phone: '',
    tipo: 'DESCARREGAMENTO'
  });
  // Add new state for the elegant confirmation modal
  const [showNewLoadModal, setShowNewLoadModal] = useState(false);
  const [truckForNewLoad, setTruckForNewLoad] = useState(null);
  const blinkingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  
  // Get user role
  const user = JSON.parse(localStorage.getItem('user'));
  const isOperador = user && user.role === 'Operador';
  const isSupervisor = user && user.role === 'Supervisor';

  // Check if user can initiate descarga (Supervisor cannot)
  const canInitiateDescarga = true;

  // Check if user can release manifest (Supervisor cannot)
  const canReleaseManifest = true;

  // Check if user can perform checkout (Operador cannot)
  const canPerformCheckout = !isOperador;
  
  // Enhanced data update function that ensures consistency
  const updateDataConsistently = useCallback((newTrucks, newCheckers, newPartners) => {
    console.log('Dashboard: Updating data consistently');
    
    // Update the ref with the latest data
    latestDataRef.current = {
      trucks: newTrucks,
      checkers: newCheckers,
      partners: newPartners
    };
    
    // Update state with a small delay to prevent flickering
    setTimeout(() => {
      setTrucks(prev => {
        // Only update if data actually changed
        if (JSON.stringify(prev) !== JSON.stringify(newTrucks)) {
          console.log('Dashboard: Trucks state updated');
          return newTrucks;
        }
        return prev;
      });
      
      setCheckers(prev => {
        // Only update if data actually changed
        if (JSON.stringify(prev) !== JSON.stringify(newCheckers)) {
          console.log('Dashboard: Checkers state updated');
          return newCheckers;
        }
        return prev;
      });
      
      setPartners(prev => {
        // Only update if data actually changed
        if (JSON.stringify(prev) !== JSON.stringify(newPartners)) {
          console.log('Dashboard: Partners state updated');
          return newPartners;
        }
        return prev;
      });
    }, 0);
  }, []);
  
  // Fetch data function with consistency checks
  const fetchData = useCallback(async () => {
    try {
      console.log('Dashboard: Starting data fetch');
      
      // Fetch data
      const [trucksRes, checkersRes, partnersRes] = await Promise.all([
        api.get('/trucks'),
        api.get('/checkers'),
        api.get('/partners')
      ]);
      
      console.log('Dashboard: Data fetched successfully');
      console.log('Dashboard: Trucks count:', trucksRes.data.length);
      
      // Update data consistently
      updateDataConsistently(trucksRes.data, checkersRes.data, partnersRes.data);
      
    } catch (error) {
      console.error('Dashboard: Error fetching data:', error);
      console.error('Dashboard: Error details:', error.response?.data || error.message);
      
      // On error, try to use the latest known good data
      if (latestDataRef.current.trucks.length > 0) {
        console.log('Dashboard: Using cached data due to error');
        updateDataConsistently(
          latestDataRef.current.trucks,
          latestDataRef.current.checkers,
          latestDataRef.current.partners
        );
      }
    }
  }, [updateDataConsistently]);
  
  // Initialize socket connection
  const initSocket = useCallback(() => {
    if (!socket) {
      socket = io('https://nblpatio.nbl.dev.br');
      
      socket.on('connect', () => {
        console.log('Dashboard: Socket connected');
      });
      
      socket.on('truckUpdated', (updatedTruck) => {
        console.log('Dashboard: Received truck update via socket', updatedTruck);
        
        // Update the trucks list with the updated truck
        setTrucks(prevTrucks => {
          const truckIndex = prevTrucks.findIndex(t => t._id === updatedTruck._id);
          let newTrucks;
          
          if (truckIndex !== -1) {
            // Truck exists, update it
            newTrucks = [...prevTrucks];
            newTrucks[truckIndex] = updatedTruck;
          } else {
            // New truck, add it
            newTrucks = [...prevTrucks, updatedTruck];
          }
          
          // Update the ref with the latest data
          latestDataRef.current.trucks = newTrucks;
          return newTrucks;
        });
      });
      
      // Handle truck deletion events
      socket.on('truckDeleted', (deletedTruck) => {
        console.log('Dashboard: Received truck deletion via socket', deletedTruck);
        
        // Remove the deleted truck from the trucks list
        setTrucks(prevTrucks => {
          const newTrucks = prevTrucks.filter(t => t._id !== deletedTruck.id);
          
          // Update the ref with the latest data
          latestDataRef.current.trucks = newTrucks;
          return newTrucks;
        });
      });
      
      socket.on('disconnect', () => {
        console.log('Dashboard: Socket disconnected');
      });
    }
  }, []);
  
  // Fetch initial data
  useEffect(() => {
    console.log('Dashboard: Component mounted, starting initial data fetch');
    fetchData();
    
    // Initialize socket connection for real-time updates
    initSocket();
    
    // Set up polling interval as fallback (every 30 seconds)
    console.log('Dashboard: Setting up polling interval');
    pollingIntervalRef.current = setInterval(() => {
      console.log('Dashboard: Polling interval triggered');
      fetchData();
    }, 30000);
    
    // Listen for company change events
    const handleCompanyChange = () => {
      console.log('Dashboard: Company change event detected');
      fetchData();
    };
    
    console.log('Dashboard: Adding company change event listener');
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      console.log('Dashboard: Component unmounting, cleaning up');
      if (pollingIntervalRef.current) {
        console.log('Dashboard: Clearing polling interval');
        clearInterval(pollingIntervalRef.current);
      }
      console.log('Dashboard: Removing company change event listener');
      window.removeEventListener('companyChanged', handleCompanyChange);
      
      // Clean up socket connection
      if (socket) {
        socket.disconnect();
        socket = null;
      }
    };
  }, [fetchData, initSocket]);

  // Handle blinking trucks for checkout
  useEffect(() => {
    console.log('Dashboard: Blinking effect useEffect triggered, activeTab:', activeTab, 'trucks count:', trucks.length);
    
    if (activeTab === 'checkout') {
      const checkoutTrucks = trucks.filter(truck => 
        truck && truck.status === 'CONCLUIDO' && 
        truck.tipo === 'DESCARREGAMENTO'
      );
      
      console.log('Dashboard: Checkout trucks found:', checkoutTrucks.length);
      
      if (checkoutTrucks.length > 0) {
        // Clear any existing interval
        if (blinkingIntervalRef.current) {
          console.log('Dashboard: Clearing existing blinking interval');
          clearInterval(blinkingIntervalRef.current);
        }
        
        // Set up interval to blink trucks
        let currentIndex = 0;
        setBlinkingTruck(checkoutTrucks[0]);
        console.log('Dashboard: Setting initial blinking truck:', checkoutTrucks[0]);
        
        blinkingIntervalRef.current = setInterval(() => {
          currentIndex = (currentIndex + 1) % checkoutTrucks.length;
          setBlinkingTruck(checkoutTrucks[currentIndex]);
          console.log('Dashboard: Blinking truck updated to index:', currentIndex);
        }, 60000); // 1 minute
      } else {
        // Clear interval if no checkout trucks
        if (blinkingIntervalRef.current) {
          console.log('Dashboard: Clearing blinking interval - no checkout trucks');
          clearInterval(blinkingIntervalRef.current);
        }
        setBlinkingTruck(null);
      }
    } else {
      // Clear interval if not on checkout tab
      if (blinkingIntervalRef.current) {
        console.log('Dashboard: Clearing blinking interval - not on checkout tab');
        clearInterval(blinkingIntervalRef.current);
      }
      setBlinkingTruck(null);
    }
  }, [activeTab, trucks]);

  // Filter trucks based on active tab
  const getFilteredTrucks = useCallback(() => {
    console.log('Dashboard: Filtering trucks, activeTab:', activeTab, 'total trucks:', trucks.length);
    
    // Get base filtered trucks
    let baseTrucks;
    switch (activeTab) {
      case 'aguardando':
        baseTrucks = trucks.filter(truck => truck && truck.status === 'AGUARDANDO');
        break;
      case 'descarga':
        baseTrucks = trucks.filter(truck => truck && truck.status === 'EM_DESCARGA');
        break;
      case 'concluidos':
        baseTrucks = trucks.filter(truck => truck && truck.status === 'CONCLUIDO' && 
          truck.tipo === 'DESCARREGAMENTO');
        break;
      case 'giro':
        baseTrucks = trucks.filter(truck => truck && truck.status === 'CONCLUIDO' && 
          truck.tipo === 'GIRO');
        break;
      case 'captacao':
        baseTrucks = trucks.filter(truck => truck && truck.status === 'CONCLUIDO' && 
          truck.tipo === 'CAPTAÇÃO');
        break;
      case 'agregado':
        baseTrucks = trucks.filter(truck => truck && truck.status === 'CONCLUIDO' && 
          truck.tipo === 'AGREGADO');
        break;
      default:
        // For the general view, only show trucks in active states (exclude CHECKOUT status)
        baseTrucks = trucks.filter(truck => truck && truck.status !== 'CHECKOUT');
    }
    
    console.log('Dashboard: Base filtered trucks count:', baseTrucks.length);
    
    // Apply time filter only for giro, captacao, and agregado tabs
    if ((activeTab === 'giro' || activeTab === 'captacao' || activeTab === 'agregado') && timeFilter !== 'tudo') {
      console.log('Dashboard: Applying time filter:', timeFilter);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (timeFilter) {
        case 'hoje':
          // Filter for today
          baseTrucks = baseTrucks.filter(truck => {
            if (!truck) return false;
            const endDate = truck.endTime ? new Date(truck.endTime) : new Date(truck.entryTime);
            const endDateDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            return endDateDate.getTime() === today.getTime();
          });
          break;
        case 'semana':
          // Filter for current week (Sunday to Saturday)
          const firstDayOfWeek = new Date(today);
          firstDayOfWeek.setDate(today.getDate() - today.getDay());
          const lastDayOfWeek = new Date(firstDayOfWeek);
          lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
          
          baseTrucks = baseTrucks.filter(truck => {
            if (!truck) return false;
            const endDate = truck.endTime ? new Date(truck.endTime) : new Date(truck.entryTime);
            const endDateDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            return endDateDate >= firstDayOfWeek && endDateDate <= lastDayOfWeek;
          });
          break;
        case 'mes':
          // Filter for current month
          baseTrucks = baseTrucks.filter(truck => {
            if (!truck) return false;
            const endDate = truck.endTime ? new Date(truck.endTime) : new Date(truck.entryTime);
            return endDate.getMonth() === now.getMonth() && endDate.getFullYear() === now.getFullYear();
          });
          break;
        default:
          break;
      }
    }
    
    console.log('Dashboard: Final filtered trucks count:', baseTrucks.length);
    return baseTrucks;
  }, [trucks, activeTab, timeFilter]);

  const filteredTrucks = useMemo(() => {
    const result = getFilteredTrucks();
    console.log('Dashboard: Filtered trucks result:', result.length);
    return result;
  }, [getFilteredTrucks]);

  // Group trucks for Kanban view
  const getTrucksByStatus = useCallback((status) => {
    const result = trucks.filter(truck => truck && truck.status === status);
    console.log('Dashboard: Trucks by status', status, ':', result.length);
    return result;
  }, [trucks]);

  // Open modal for moving truck to descarga
  const openMoveToDescargaModal = (truckId) => {
    const truck = trucks.find(t => t._id === truckId);
    setCurrentTruckId(truckId);
    setMoveToDescargaData({
      dockNumber: '',
      checker: '',
      volume: '',
      truckInfo: {
        plate: truck?.trailerPlate,
        driver: truck?.driverName,
        partner: truck?.partner?.name
      }
    });
    setShowMoveToDescargaModal(true);
  };

  // Close modal
  const closeMoveToDescargaModal = () => {
    setShowMoveToDescargaModal(false);
    setCurrentTruckId(null);
    setMoveToDescargaData({
      dockNumber: '',
      checker: '',
      volume: ''
    });
  };

  // Handle input changes in modal
  const handleMoveToDescargaInputChange = (e) => {
    const { name, value } = e.target;
    setMoveToDescargaData({
      ...moveToDescargaData,
      [name]: value
    });
  };

  // Handle move to descarga submission
  const handleMoveToDescargaSubmit = async (e) => {
    e.preventDefault();
    const { dockNumber, checker, volume } = moveToDescargaData;
    
    if (dockNumber && checker && volume) {
      try {
        // Immediately update UI state
        setTrucks(prevTrucks => {
          return prevTrucks.map(truck => {
            if (truck._id === currentTruckId) {
              return {
                ...truck,
                status: 'EM_DESCARGA',
                dockNumber: parseInt(dockNumber),
                checker: checker,
                volume: parseInt(volume),
                startTime: new Date()
              };
            }
            return truck;
          });
        });
        
        // Make API call
        await api.put(`/trucks/${currentTruckId}/status`, {
          status: 'EM_DESCARGA',
          dockNumber: dockNumber,
          checker: checker,
          volume: parseInt(volume)
        });
        
        closeMoveToDescargaModal();
      } catch (error) {
        console.error('Error updating truck status:', error);
        // Revert UI state on error
        setTrucks(prevTrucks => {
          return prevTrucks.map(truck => {
            if (truck._id === currentTruckId) {
              return {
                ...truck,
                status: 'AGUARDANDO',
                dockNumber: undefined,
                checker: undefined,
                volume: undefined,
                startTime: undefined
              };
            }
            return truck;
          });
        });
        alert('Erro ao iniciar descarga: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleCompleteDescarga = async (truckId) => {
    try {
      // Immediately update UI state
      setTrucks(prevTrucks => {
        return prevTrucks.map(truck => {
          if (truck._id === truckId) {
            return {
              ...truck,
              status: 'CONCLUIDO',
              endTime: new Date()
            };
          }
          return truck;
        });
      });
      
      // Make API call
      await api.put(`/trucks/${truckId}/status`, {
        status: 'CONCLUIDO'
      });
    } catch (error) {
      console.error('Error updating truck status:', error);
      // Revert UI state on error
      setTrucks(prevTrucks => {
        return prevTrucks.map(truck => {
          if (truck._id === truckId) {
            return {
              ...truck,
              status: 'EM_DESCARGA',
              endTime: undefined
            };
          }
          return truck;
        });
      });
      alert('Erro ao finalizar descarga: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCompleteCheckout = async (truckId) => {
    try {
      // Immediately update UI state
      setTrucks(prevTrucks => {
        return prevTrucks.map(truck => {
          if (truck._id === truckId) {
            return {
              ...truck,
              status: 'CHECKOUT',
              exitTime: new Date()
            };
          }
          return truck;
        });
      });
      
      // Make API call
      await api.put(`/trucks/${truckId}/checkout`, {});
    } catch (error) {
      console.error('Error completing checkout:', error);
      // Revert UI state on error
      setTrucks(prevTrucks => {
        return prevTrucks.map(truck => {
          if (truck._id === truckId) {
            return {
              ...truck,
              status: 'CONCLUIDO',
              exitTime: undefined
            };
          }
          return truck;
        });
      });
      alert('Erro ao finalizar checkout: ' + (error.response?.data?.message || error.message));
    }
  };

  // New function to handle moving to AGUARDANDO_DESCARREGAMENTO status
  const handleMoveToAguardandoDescarregamento = async (truckId) => {
    // Find the truck object
    const truck = trucks.find(t => t._id === truckId);
    if (truck) {
      // Show elegant confirmation modal instead of basic confirm dialog
      setTruckForNewLoad(truck);
      setShowNewLoadModal(true);
    }
  };

  // New function to handle moving to CONCLUIDO status
  const handleMoveToConcluido = async (truckId) => {
    try {
      // Immediately update UI state
      setTrucks(prevTrucks => {
        return prevTrucks.map(truck => {
          if (truck._id === truckId) {
            return {
              ...truck,
              status: 'CONCLUIDO',
              endTime: new Date()
            };
          }
          return truck;
        });
      });
      
      // Make API call
      await api.put(`/trucks/${truckId}/status`, {
        status: 'CONCLUIDO'
      });
    } catch (error) {
      console.error('Error updating truck status:', error);
      // Revert UI state on error
      setTrucks(prevTrucks => {
        return prevTrucks.map(truck => {
          if (truck._id === truckId) {
            return {
              ...truck,
              status: 'AGUARDANDO_DESCARREGAMENTO',
              endTime: undefined
            };
          }
          return truck;
        });
      });
      alert('Erro ao mover para Concluído: ' + (error.response?.data?.message || error.message));
    }
  };

  // Function to handle the new load confirmation
  const handleNewLoadConfirmation = async (hasNewLoad) => {
    setShowNewLoadModal(false);
    
    if (hasNewLoad) {
      try {
        // Immediately update UI state
        setTrucks(prevTrucks => {
          return prevTrucks.map(truck => {
            if (truck._id === truckForNewLoad._id) {
              return {
                ...truck,
                status: 'AGUARDANDO_DESCARREGAMENTO',
                endTime: new Date()
              };
            }
            return truck;
          });
        });
        
        // Make API call
        await api.put(`/trucks/${truckForNewLoad._id}/status`, {
          status: 'AGUARDANDO_DESCARREGAMENTO'
        });
      } catch (error) {
        console.error('Error updating truck status:', error);
        // Revert UI state on error
        setTrucks(prevTrucks => {
          return prevTrucks.map(truck => {
            if (truck._id === truckForNewLoad._id) {
              return {
                ...truck,
                status: 'CONCLUIDO',
                endTime: undefined
              };
            }
            return truck;
          });
        });
        alert('Erro ao mover para Aguardando Descarregamento: ' + (error.response?.data?.message || error.message));
      }
    } else {
      // Continue with normal checkout flow - move to CONCLUIDO first
      handleMoveToConcluido(truckForNewLoad._id);
    }
    
    // Clear the truck for new load
    setTruckForNewLoad(null);
  };

  // Handle release/unrelease for manifest
  const handleToggleManifestRelease = async (truckId, currentlyReleased) => {
    try {
      // Immediately update UI state
      setTrucks(prevTrucks => {
        return prevTrucks.map(truck => {
          if (truck._id === truckId) {
            return {
              ...truck,
              releasedForManifest: !currentlyReleased
            };
          }
          return truck;
        });
      });
      
      // Make API call
      await api.put(`/trucks/${truckId}/status`, {
        releasedForManifest: !currentlyReleased
      });
    } catch (error) {
      console.error('Error updating manifest release status:', error);
      // Revert UI state on error
      setTrucks(prevTrucks => {
        return prevTrucks.map(truck => {
          if (truck._id === truckId) {
            return {
              ...truck,
              releasedForManifest: currentlyReleased
            };
          }
          return truck;
        });
      });
      alert('Erro ao atualizar status de liberação: ' + (error.response?.data?.message || error.message));
    }
  };

  // Sort trucks by queue number for AGUARDANDO status
  const sortAguardandoTrucks = (trucksList) => {
    return [...trucksList].sort((a, b) => {
      const queueA = a.queueNumber || 0;
      const queueB = b.queueNumber || 0;
      return queueA - queueB;
    });
  };

  // Toggle dropdown menu
  const toggleDropdown = (truckId) => {
    if (dropdownOpen === truckId) {
      setDropdownOpen(null);
    } else {
      setDropdownOpen(truckId);
      // Fix dropdown position for table views
      setTimeout(() => {
        const dropdown = document.querySelector(`[data-dropdown="${truckId}"] .dropdown-content`);
        if (dropdown) {
          const rect = dropdown.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Adjust position if dropdown goes outside viewport
          if (rect.right > viewportWidth) {
            dropdown.style.left = 'auto';
            dropdown.style.right = '0';
          }
          
          if (rect.bottom > viewportHeight) {
            dropdown.style.top = 'auto';
            dropdown.style.bottom = '100%';
          }
        }
      }, 0);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-menu')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Handle truck reversal
  const handleReverseStatus = async (truckId, currentStatus) => {
    try {
      let newStatus;
      switch (currentStatus) {
        case 'EM_DESCARGA':
          newStatus = 'AGUARDANDO';
          break;
        case 'CONCLUIDO':
          newStatus = 'EM_DESCARGA';
          break;
        default:
          return; // No reversal for other statuses
      }

      await api.put(`/trucks/${truckId}/status`, {
        status: newStatus
      });
      
      setDropdownOpen(null); // Close dropdown after action
    } catch (error) {
      console.error('Error reversing truck status:', error);
      alert('Erro ao reverter status do caminhão: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle truck deletion
  const handleDeleteTruck = async (truckId) => {
    if (window.confirm('Tem certeza que deseja excluir este caminhão?')) {
      try {
        await api.delete(`/trucks/${truckId}`);
        setDropdownOpen(null); // Close dropdown after action
        
        // Immediately remove the truck from the local state
        setTrucks(prevTrucks => {
          const newTrucks = prevTrucks.filter(t => t._id !== truckId);
          
          // Update the ref with the latest data
          latestDataRef.current.trucks = newTrucks;
          return newTrucks;
        });
      } catch (error) {
        console.error('Error deleting truck:', error);
        alert('Erro ao excluir caminhão: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  // Open edit modal
  const openEditModal = (truck) => {
    setEditingTruck(truck);
    setEditFormData({
      trailerPlate: truck.trailerPlate || '',
      truckPlate: truck.truckPlate || '',
      driverName: truck.driverName || '',
      cpf: truck.cpf || '',
      partner: truck.partner?._id || truck.partner || '',
      phone: truck.phone || '',
      tipo: truck.tipo || 'DESCARREGAMENTO'
    });
    setShowEditModal(true);
    setDropdownOpen(null); // Close dropdown
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingTruck(null);
    setEditFormData({
      trailerPlate: '',
      truckPlate: '',
      driverName: '',
      cpf: '',
      partner: '',
      phone: '',
      tipo: 'DESCARREGAMENTO'
    });
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedTruck = {
        ...editFormData,
        partner: editFormData.partner // Ensure partner is sent as ID
      };
      
      await api.put(`/trucks/${editingTruck._id}`, updatedTruck);
      closeEditModal();
      alert('Caminhão atualizado com sucesso!');
    } catch (error) {
      console.error('Error updating truck:', error);
      alert('Erro ao atualizar caminhão: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="dashboard-container">
      <div className="tabs">
        
        <button 
          className={`tab ${activeTab === 'aguardando' ? 'active' : ''}`}
          onClick={() => setActiveTab('aguardando')}
        >
          🔴 Aguardando
        </button>
        <button 
          className={`tab ${activeTab === 'descarga' ? 'active' : ''}`}
          onClick={() => setActiveTab('descarga')}
        >
          🟡 Em Descarga
        </button>
        <button 
          className={`tab ${activeTab === 'concluidos' ? 'active' : ''}`}
          onClick={() => setActiveTab('concluidos')}
        >
          🟢 Concluídos
        </button>
        <button 
          className={`tab ${activeTab === 'aguardando_carregamento' ? 'active' : ''}`}
          onClick={() => setActiveTab('aguardando_carregamento')}
        >
          🔵 Aguardando Carregamento
        </button>
        <button 
          className={`tab ${activeTab === 'giro' ? 'active' : ''}`}
          onClick={() => setActiveTab('giro')}
        >
          🔄 Giro
        </button>
        <button 
          className={`tab ${activeTab === 'captacao' ? 'active' : ''}`}
          onClick={() => setActiveTab('captacao')}
        >
          🚚 Captação
        </button>
        <button 
          className={`tab ${activeTab === 'agregado' ? 'active' : ''}`}
          onClick={() => setActiveTab('agregado')}
        >
          📦 Agregado
        </button>
        <button 
          className={`tab ${activeTab === 'checkout' ? 'active' : ''}`}
          onClick={() => setActiveTab('checkout')}
        >
          💵 Checkout
        </button>
      </div>

      {/* Modal for moving to descarga - Enhanced Elegant Design */}
      {showMoveToDescargaModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>XMLLoader Iniciar Descarga</h3>
              <button className="modal-close" onClick={closeMoveToDescargaModal}>×</button>
            </div>
            <form onSubmit={handleMoveToDescargaSubmit} className="modal-body">
              {/* Truck Information Display */}
              <div className="truck-info-display">
                <h4>Informações do Veículo</h4>
                <div className="info-row">
                  <span className="info-label">Placa:</span>
                  <span className="info-value plate-highlight">{moveToDescargaData.truckInfo?.plate}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Motorista:</span>
                  <span className="info-value">{moveToDescargaData.truckInfo?.driver || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Parceiro:</span>
                  <span className="info-value">{moveToDescargaData.truckInfo?.partner || 'N/A'}</span>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="dockNumber">Número da Doca *</label>
                <input
                  type="number"
                  id="dockNumber"
                  name="dockNumber"
                  value={moveToDescargaData.dockNumber}
                  onChange={handleMoveToDescargaInputChange}
                  placeholder="Ex: 1, 2, 3..."
                  required
                  min="1"
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="checker">Conferente *</label>
                <select
                  id="checker"
                  name="checker"
                  value={moveToDescargaData.checker}
                  onChange={handleMoveToDescargaInputChange}
                  required
                >
                  <option value="">Selecione um conferente</option>
                  {checkers.filter(c => c.status === 'ATIVO').map(checker => (
                    <option key={checker._id} value={checker._id}>
                      {checker?.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="volume">Volume *</label>
                <input
                  type="number"
                  id="volume"
                  name="volume"
                  value={moveToDescargaData.volume}
                  onChange={handleMoveToDescargaInputChange}
                  placeholder="Ex: 100, 250, 500..."
                  required
                  min="1"
                />
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeMoveToDescargaModal}>
                  ❌ Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={!moveToDescargaData.dockNumber || !moveToDescargaData.checker || !moveToDescargaData.volume}
                >
                  ✅ Iniciar Descarga
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Truck Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>✏️ Editar Caminhão</h3>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <form onSubmit={handleEditSubmit} className="modal-body">
              <div className="form-group">
                <label htmlFor="trailerPlate">Placa da Carreta *</label>
                <input
                  type="text"
                  id="trailerPlate"
                  name="trailerPlate"
                  value={editFormData.trailerPlate}
                  onChange={handleEditInputChange}
                  placeholder="Ex: AAA1234"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="truckPlate">Placa do Cavalo</label>
                <input
                  type="text"
                  id="truckPlate"
                  name="truckPlate"
                  value={editFormData.truckPlate}
                  onChange={handleEditInputChange}
                  placeholder="Ex: BBB5678"
                  
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="driverName">Nome do Motorista *</label>
                <input
                  type="text"
                  id="driverName"
                  name="driverName"
                  value={editFormData.driverName}
                  onChange={handleEditInputChange}
                  placeholder="Ex: João Silva"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="cpf">CPF do Motorista</label>
                <input
                  type="text"
                  id="cpf"
                  name="cpf"
                  value={editFormData.cpf}
                  onChange={handleEditInputChange}
                  placeholder="Ex: 123.456.789-00"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="partner">Parceiro *</label>
                <select
                  id="partner"
                  name="partner"
                  value={editFormData.partner}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="">Selecione um parceiro</option>
                  {partners.map(partner => (
                    <option key={partner._id} value={partner._id}>
                      {partner?.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Telefone</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={editFormData.phone}
                  onChange={handleEditInputChange}
                  placeholder="Ex: +5591985149156"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="tipo">Tipo *</label>
                <select
                  id="tipo"
                  name="tipo"
                  value={editFormData.tipo}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="DESCARREGAMENTO">DESCARREGAMENTO</option>
                  <option value="GIRO">GIRO</option>
                  <option value="CAPTAÇÃO">CAPTAÇÃO</option>
                  <option value="AGREGADO">AGREGADO</option>
                  <option value="CARREGAMENTO">CARREGAMENTO</option>
                </select>
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeEditModal}>
                  ❌ Cancelar
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                >
                  ✅ Salvar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'checkout' ? (
        <div className="checkout-dashboard">
          {/* Blinking notification for checkout trucks - only shown on checkout tab */}
          {blinkingTruck && (
            <div className="checkout-notification">
              <div className="notification-content">
                <h2>🚚 DESCARREGAMENTO FINALIZADO!</h2>
                <div className="blinking-plate">
                  <span>Placa Carreta:</span>
                  <span className="plate-number">{blinkingTruck.trailerPlate}</span>
                  <span>Placa Cavalo:</span>
                  <span className="plate-number">{blinkingTruck.truckPlate || 'N/A'}</span>
                </div>
                <p>Motorista deve retirar veículo do pátio</p>
              </div>
            </div>
          )}
          
          <h2>Veículos Disponíveis para Checkout</h2>
          <div className="checkout-trucks-list">
            {trucks.filter(truck => truck && truck.status === 'CONCLUIDO' && truck.tipo === 'DESCARREGAMENTO').length === 0 ? (
              <p className="no-trucks">Nenhum veículo disponível para checkout</p>
            ) : (
              trucks
                .filter(truck => truck && truck.status === 'CONCLUIDO' && truck.tipo === 'DESCARREGAMENTO')
                .map(truck => (
                  <div key={truck._id} className="checkout-truck-item">
                    <div className="truck-info">
                      <span className="plate">
                        <div>Carreta: {truck.trailerPlate || 'N/A'}</div>
                        <div>Cavalo: {truck.truckPlate || 'N/A'}</div>
                      </span>
                      <span className="partner">{ (truck.partner && truck?.partner?.name) }</span>
                      <span className="time">Concluído: {truck.endTime ? formatBrasiliaDate(truck.endTime) : 'N/A'}</span>
                      {truck.status === 'AGUARDANDO_DESCARREGAMENTO' && (
                        <span className="status-badge aguardando-carregamento">Aguardando Carregamento</span>
                      )}
                    </div>
                    <button 
                      className="btn-success"
                      onClick={() => handleCompleteCheckout(truck._id)}
                    >
                      Finalizar Checkout
                    </button>
                  </div>
                ))
            )}
          </div>
        </div>
      ) : activeTab === 'geral' ? (
        <div className="kanban-board">
          <div className="kanban-column">
            <h3>AGUARDANDO DESCARGA ({getTrucksByStatus('AGUARDANDO').length})</h3>
            <div className="kanban-cards">
              {sortAguardandoTrucks(getTrucksByStatus('AGUARDANDO')).map(truck => (
                <div key={truck._id} className="kanban-card aguardando">
                  <div className="card-header">
                    <span className="order-number">#{truck.queueNumber || 'N/A'}</span>
                    <span className="plate">
                      <div>Carreta: {truck.trailerPlate || 'N/A'}</div>
                      <div>Cavalo: {truck.truckPlate || 'N/A'}</div>
                    </span>
                    <div className="dropdown-menu">
                      <button 
                        className="dropdown-toggle"
                        onClick={() => toggleDropdown(truck._id)}
                      >
                        ⋮
                      </button>
                      {dropdownOpen === truck._id && (
                        <div className="dropdown-content">
                          <button onClick={() => openEditModal(truck)}>
                            Editar
                          </button>
                          <hr className="dropdown-divider" />
                          <button 
                            className="danger"
                            onClick={() => handleDeleteTruck(truck._id)}
                          >
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="card-body">
                    <p><span className="label">Motorista:</span> <span className="value">{truck.driverName ? truck.driverName.split(' ')[0] : '-'}</span></p>
                    <p><span className="label">Parceiro:</span> <span className="value">{truck.partner?.name ? truck.partner.name.split(' ')[0] : '-'}</span></p>
                    <p><span className="label">Conferente:</span> <span className="value">{typeof truck.checker === 'object' ? truck.checker?.name : (checkers?.find(c => c._id === truck.checker)?.name || '-')}</span></p>
                    <p><span className="label">Doca:</span> <span className="value">{truck.dockNumber || '-'}</span></p>
                    <p><span className="label">Volume:</span> <span className="value">{truck.volume ? Number(truck.volume).toLocaleString('pt-BR') : '-'}</span></p>
                    <p><span className="label">Início:</span> <span className="value">{truck.startTime ? formatBrasiliaDate(truck.startTime) : '-'}</span></p>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="btn-primary"
                      onClick={() => openMoveToDescargaModal(truck._id)}
                    >
                      Iniciar Descarga
                    </button>
                    <button 
                      className={`btn-${truck.releasedForManifest ? 'warning' : 'success'}`}
                      onClick={() => handleToggleManifestRelease(truck._id, truck.releasedForManifest)}
                    >
                      {truck.releasedForManifest ? 'Bloquear Entrada' : 'Liberar Entrada'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="kanban-column">
            <h3>EM DESCARGA ({getTrucksByStatus('EM_DESCARGA').length})</h3>
            <div className="kanban-cards">
              {getTrucksByStatus('EM_DESCARGA').map(truck => (
                <div key={truck._id} className="kanban-card descarga">
                  <div className="card-header">
                    <span className="order-number">#{truck.descargaOrderNumber || 'N/A'}</span>
                    <span className="plate">
                      <div>Carreta: {truck.trailerPlate || 'N/A'}</div>
                      <div>Cavalo: {truck.truckPlate || 'N/A'}</div>
                    </span>
                    <span className="dock">Doca {truck.dockNumber}</span>
                    <div className="dropdown-menu">
                      <button 
                        className="dropdown-toggle"
                        onClick={() => toggleDropdown(truck._id)}
                      >
                        ⋮
                      </button>
                      {dropdownOpen === truck._id && (
                        <div className="dropdown-content">
                          <button onClick={() => openEditModal(truck)}>
                            Editar
                          </button>
                          <hr className="dropdown-divider" />
                          <button 
                            onClick={() => handleReverseStatus(truck._id, 'EM_DESCARGA')}
                          >
                            Voltar para Aguardando
                          </button>
                          <hr className="dropdown-divider" />
                          <button 
                            className="danger"
                            onClick={() => handleDeleteTruck(truck._id)}
                          >
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="card-body">
                    <p><span className="label">Motorista:</span> <span className="value">{truck.driverName ? truck?.driverName?.split(' ')[0] : 'N/A'}</span></p>
                    <p><span className="label">Parceiro:</span> <span className="value">{ (truck.partner && truck?.partner?.name)  ?  (truck.partner && truck?.partner?.name).split(' ')[0] : 'N/A'}</span></p>
                    <p><span className="label">Conferente:</span> <span className="value">{(typeof truck.checker === 'object' ?  (truck.checker && truck?.checker?.name)  :  (checkers && checkers.find(c => c && c._id === truck.checker) && checkers.find(c => c && c._id === truck.checker).name) ) || 'N/A'}</span></p>
                    <p><span className="label">Volume:</span> <span className="value">{truck.volume || 'N/A'}</span></p>
                    <p><span className="label">Início:</span> <span className="value">{truck.startTime ? formatBrasiliaDate(truck.startTime) : 'N/A'}</span></p>
                  </div>
                  <div className="card-actions">
                    <button 
                      className="btn-success"
                      onClick={() => handleMoveToAguardandoDescarregamento(truck._id)}
                    >
                      Finalizar Descarga
                    </button>
                  </div>                </div>
              ))}
            </div>
          </div>
          
          {/* Hide Concluídos column in geral view */}
          <div className="kanban-column" style={{ display: 'none' }}>
            <h3>CONCLUÍDOS ({getTrucksByStatus('CONCLUIDO').length})</h3>
            <div className="kanban-cards">
              {getTrucksByStatus('CONCLUIDO').map(truck => (
                <div key={truck._id} className="kanban-card concluido">
                  <div className="card-header">
                    <span className="plate">
                      <div>Carreta: {truck.trailerPlate || 'N/A'}</div>
                      <div>Cavalo: {truck.truckPlate || 'N/A'}</div>
                    </span>
                    <div className="dropdown-menu">
                      <button 
                        className="dropdown-toggle"
                        onClick={() => toggleDropdown(truck._id)}
                      >
                        ⋮
                      </button>
                      {dropdownOpen === truck._id && (
                        <div className="dropdown-content">
                          <button onClick={() => openEditModal(truck)}>
                            Editar
                          </button>
                          <hr className="dropdown-divider" />
                          <button 
                            onClick={() => handleReverseStatus(truck._id, 'CONCLUIDO')}
                          >
                            Voltar para Em Descarga
                          </button>
                          <hr className="dropdown-divider" />
                          <button 
                            className="danger"
                            onClick={() => handleDeleteTruck(truck._id)}
                          >
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="card-body">
                    <p><span className="label">Motorista:</span> <span className="value">{truck.driverName ? truck?.driverName?.split(' ')[0] : 'N/A'}</span></p>
                    <p><span className="label">Parceiro:</span> <span className="value">{ (truck.partner && truck?.partner?.name)  ?  (truck.partner && truck?.partner?.name).split(' ')[0] : 'N/A'}</span></p>
                    <p><span className="label">Concluído em:</span> <span className="value">{truck.endTime ? formatBrasiliaDate(truck.endTime) : 'N/A'}</span></p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : activeTab === 'aguardando_carregamento' ? (
        <div className="list-view">
          <div className="list-header">
            <h2>Veículos Aguardando Carregamento</h2>
          </div>
          
          {filteredTrucks.filter(truck => truck && truck.status === 'AGUARDANDO_DESCARREGAMENTO').length === 0 ? (
            <p className="no-trucks">Nenhum veículo encontrado</p>
          ) : (
            <div className="trucks-table-container">
              <table className="trucks-table">
                <thead>
                  <tr>
                    <th>Placa</th>
                    <th>Motorista</th>
                    <th>Parceiro</th>
                    <th>Concluído em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrucks
                    .filter(truck => truck && truck.status === 'AGUARDANDO_DESCARREGAMENTO')
                    .map(truck => (
                      <tr key={truck._id}>
                        <td className="truck-plate centered">
                          <div>
                            <div>Carreta: {truck.trailerPlate || 'N/A'}</div>
                            <div>Cavalo: {truck.truckPlate || 'N/A'}</div>
                          </div>
                        </td>
                        <td>{truck.driverName ? truck?.driverName?.split(' ')[0] : 'N/A'}</td>
                        <td>{ (truck.partner && truck?.partner?.name)  ?  (truck.partner && truck?.partner?.name).split(' ')[0] : 'N/A'}</td>
                        <td>{truck.endTime ? formatBrasiliaDate(truck.endTime) : 'N/A'}</td>
                        <td>
                          <div className="action-buttons" data-dropdown={truck._id}>
                            <div className="dropdown-menu">
                              <button 
                                className="dropdown-toggle"
                                onClick={() => toggleDropdown(truck._id)}
                              >
                                ⋮
                              </button>
                              {dropdownOpen === truck._id && (
                                <div className="dropdown-content">
                                  <button onClick={() => openEditModal(truck)}>
                                    Editar
                                  </button>
                                  <hr className="dropdown-divider" />
                                  <button 
                                    onClick={() => handleReverseStatus(truck._id, 'AGUARDANDO_DESCARREGAMENTO')}
                                  >
                                    Voltar para Concluído
                                  </button>
                                  <hr className="dropdown-divider" />
                                  <button 
                                    className="danger"
                                    onClick={() => handleDeleteTruck(truck._id)}
                                  >
                                    Excluir
                                  </button>
                                </div>
                              )}
                            </div>
                            {canPerformCheckout && (
                              <button 
                                className="btn-success"
                                onClick={() => handleMoveToConcluido(truck._id)}
                              >
                                Carregamento Concluído
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="list-view">
          <div className="list-header">
            <h2>
              {activeTab === 'aguardando' && 'Veículos Aguardando Descarga'}
              {activeTab === 'descarga' && 'Veículos em Descarga'}
              {activeTab === 'concluidos' && 'Veículos Concluídos'}
              {activeTab === 'giro' && 'Veículos Giro'}
              {activeTab === 'captacao' && 'Veículos Captação'}
              {activeTab === 'agregado' && 'Veículos Agregado'}
            </h2>
            
            {/* Time filter for giro, captacao, and agregado tabs */}
            {(activeTab === 'giro' || activeTab === 'captacao' || activeTab === 'agregado') && (
              <div className="time-filter">
                <select 
                  value={timeFilter} 
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="tudo">Tudo</option>
                  <option value="hoje">Hoje</option>
                  <option value="semana">Semana atual</option>
                  <option value="mes">Mês atual</option>
                </select>
              </div>
            )}
          </div>
          
          {filteredTrucks.length === 0 ? (
            <p className="no-trucks">Nenhum veículo encontrado</p>
          ) : (
            <div className="trucks-table-container">
              <table className="trucks-table">
                <thead>
                  <tr>
                    {activeTab === 'aguardando' && (
                      <th>Nº Ordem</th>
                    )}
                    <th>Placa</th>
                    <th>Motorista</th>
                    <th>Parceiro</th>
                    <th>Data de Entrada</th>
                    {activeTab === 'aguardando' && (
                      <th>Liberado Manifesto</th>
                    )}
                    {activeTab === 'descarga' && (
                      <>
                        <th>Doca</th>
                        <th>Conferente</th>
                        <th>Volume</th>
                        <th>Início da Descarga</th>
                      </>
                    )}
                    {(activeTab === 'concluidos' || activeTab === 'giro' || activeTab === 'captacao' || activeTab === 'agregado') && (
                      <th>{activeTab === 'concluidos' ? 'Fim da Descarga' : 'Saída'}</th>
                    )}
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrucks.map(truck => (
                    <tr key={truck._id} className={activeTab === 'aguardando' && truck.releasedForManifest ? 'released-manifest' : ''}>
                      {activeTab === 'aguardando' && (
                        <td>#{truck.queueNumber || 'N/A'}</td>
                      )}
                      <td className="truck-plate centered">
                        <div>
                          <div>Carreta: {truck.trailerPlate || 'N/A'}</div>
                          <div>Cavalo: {truck.truckPlate || 'N/A'}</div>
                        </div>
                      </td>
                      <td>{truck.driverName ? truck?.driverName?.split(' ')[0] : 'N/A'}</td>
                      <td>{ (truck.partner && truck?.partner?.name)  ?  (truck.partner && truck?.partner?.name).split(' ')[0] : 'N/A'}</td>
                      <td>{formatBrasiliaDate(truck.entryTime)}</td>
                      {activeTab === 'aguardando' && (
                        <td>{truck.releasedForManifest ? 'Sim' : 'Não'}</td>
                      )}
                      {activeTab === 'descarga' && (
                        <>
                          <td>{truck.dockNumber || '-'}</td>
                          <td>{typeof truck.checker === 'object' ? truck.checker?.name?.split(' ')[0] || '-' : checkers?.find(c => c._id === truck.checker)?.name?.split(' ')[0] || '-'}</td>
                          <td>{truck.volume ? Number(truck.volume).toLocaleString('pt-BR') : '-'}</td>
                          <td>{truck.startTime ? formatBrasiliaDate(truck.startTime) : '-'}</td>
                        </>
                      )}
                      
                      {(activeTab === 'concluidos' || activeTab === 'giro' || activeTab === 'captacao' || activeTab === 'agregado') && (
                        <td>{truck.endTime ? formatBrasiliaDate(truck.endTime) : 'Pendente'}</td>
                      )}
                      
                      <td>
                        <>
                          {activeTab === 'aguardando' && (
                            <div className="action-buttons" data-dropdown={`aguardando-${truck._id}`}>
                              <div className="dropdown-menu">
                                <button 
                                  className="dropdown-toggle"
                                  onClick={() => toggleDropdown(`aguardando-${truck._id}`)}
                                >
                                  ⋮
                                </button>
                                {dropdownOpen === `aguardando-${truck._id}` && (
                                  <div className="dropdown-content">
                                    <button onClick={() => openEditModal(truck)}>
                                      Editar
                                    </button>
                                    <hr className="dropdown-divider" />
                                    <button 
                                      className="danger"
                                      onClick={() => handleDeleteTruck(truck._id)}
                                    >
                                      Excluir
                                    </button>
                                  </div>
                                )}
                              </div>
                              {canInitiateDescarga && (
                                <button 
                                  className="btn-primary"
                                  onClick={() => openMoveToDescargaModal(truck._id)}
                                >
                                  Iniciar Descarga
                                </button>
                              )}
                              {canReleaseManifest && (
                                <button 
                                  className={`btn-${truck.releasedForManifest ? 'warning' : 'success'}`}
                                  onClick={() => handleToggleManifestRelease(truck._id, truck.releasedForManifest)}
                                >
                                  {truck.releasedForManifest ? 'Bloquear' : 'Liberar'}
                                </button>
                              )}
                            </div>
                          )}

                          {activeTab === 'descarga' && (
                            <div className="action-buttons" data-dropdown={`descarga-${truck._id}`}>
                              <div className="dropdown-menu">
                                <button 
                                  className="dropdown-toggle"
                                  onClick={() => toggleDropdown(`descarga-${truck._id}`)}
                                >
                                  ⋮
                                </button>
                              {dropdownOpen === `descarga-${truck._id}` && (
                                  <div className="dropdown-content">
                                    <button onClick={() => openEditModal(truck)}>
                                      Editar
                                    </button>
                                    <hr className="dropdown-divider" />
                                    <button 
                                      onClick={() => handleReverseStatus(truck._id, 'EM_DESCARGA')}
                                    >
                                      Voltar para Aguardando
                                    </button>
                                    <hr className="dropdown-divider" />
                                    <button 
                                      className="danger"
                                      onClick={() => handleDeleteTruck(truck._id)}
                                    >
                                      Excluir
                                    </button>
                                  </div>
                                )}
                              </div>
                              {canPerformCheckout && (
                                <button 
                                  className="btn-success"
                                  onClick={() => handleMoveToAguardandoDescarregamento(truck._id)}
                                >
                                  Finalizar Descarga
                                </button>
                              )}                            </div>
                          )}
                          
                          {(activeTab === 'concluidos' || activeTab === 'giro' || activeTab === 'captacao' || activeTab === 'agregado') && (
                            <div className="action-buttons" data-dropdown={truck._id}>
                              <div className="dropdown-menu">
                                <button 
                                  className="dropdown-toggle"
                                  onClick={() => toggleDropdown(truck._id)}
                                >
                                  ⋮
                                </button>
                                {dropdownOpen === truck._id && (
                                  <div className="dropdown-content">
                                    <button onClick={() => openEditModal(truck)}>
                                      Editar
                                    </button>
                                    {(activeTab === 'concluidos') && (
                                      <>
                                        <hr className="dropdown-divider" />
                                        <button 
                                          onClick={() => handleReverseStatus(truck._id, 'CONCLUIDO')}
                                        >
                                          Voltar para Em Descarga
                                        </button>
                                      </>
                                    )}
                                    <hr className="dropdown-divider" />
                                    <button 
                                      className="danger"
                                      onClick={() => handleDeleteTruck(truck._id)}
                                    >
                                      Excluir
                                    </button>
                                  </div>
                                )}
                              </div>
                              {/* Show checkout button for GIRO, CAPTAÇÃO and AGREGADO types */}
                              {(activeTab === 'giro' || activeTab === 'captacao' || activeTab === 'agregado') && canPerformCheckout && (
                                <button 
                                  className="btn-success"
                                  onClick={() => handleCompleteCheckout(truck._id)}
                                >
                                  Confirmar Saída
                                </button>
                              )}
                              {/* Show checkout button for CONCLUIDOS (only DESCARREGAMENTO) in the checkout tab */}
                              {activeTab === 'concluidos' && truck.tipo === 'DESCARREGAMENTO' && canPerformCheckout && (
                                <button 
                                  className="btn-success"
                                  onClick={() => handleCompleteCheckout(truck._id)}
                                >
                                  Finalizar Checkout
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Elegant New Load Confirmation Modal */}
      {showNewLoadModal && truckForNewLoad && (
        <div className="modal-overlay">
          <div className="modal new-load-modal">
            <div className="modal-header">
              <h3>🚚 Irá fazer carregamento?</h3>
            </div>
            <div className="modal-body">
              <div className="confirmation-message">
                <p>Esta carreta terá um <strong>novo carregamento</strong>?</p>
                <p className="small-text">Escolha uma das opções abaixo:</p>
              </div>
              
              <div className="confirmation-buttons">
                <button 
                  className="btn-warning cancel-btn"
                  onClick={() => handleNewLoadConfirmation(false)}
                >
                  ❌ Finalizar Descarregamento
                </button>
                <button 
                  className="btn-success confirm-btn"
                  onClick={() => handleNewLoadConfirmation(true)}
                >
                  ✅ Sim, irá carregar
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowNewLoadModal(false);
                  setTruckForNewLoad(null);
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;